/**
 * Job Documents API
 *
 * Endpoints:
 *   GET  /jobs/documents/:jobId
 *   POST /jobs/documents/:jobId/upload-url
 *   POST /jobs/documents/:jobId/complete
 */

import { apiClient } from '@/lib/api';
import type {
  JobDocument,
  JobDocumentCompleteBody,
  JobDocumentDownloadUrlResponse,
  JobDocumentUploadUrlBody,
  JobDocumentUploadUrlResponse,
  JobDocumentsResponse,
} from '@skewb-works/contracts';

export type { JobDocumentsResponse, JobDocument };

export interface JobDocumentsParams {
  limit?: number;
  skip?: number;
}

export async function getJobDocuments(
  jobId: number,
  params: JobDocumentsParams = {},
): Promise<JobDocumentsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('limit', String(params.limit ?? 50));
  searchParams.set('skip', String(params.skip ?? 0));

  return apiClient.get<JobDocumentsResponse>(`/jobs/documents/${jobId}?${searchParams.toString()}`);
}

export async function getJobDocumentUploadUrl(
  jobId: number,
  body: JobDocumentUploadUrlBody,
): Promise<JobDocumentUploadUrlResponse> {
  return apiClient.post<JobDocumentUploadUrlResponse>(`/jobs/documents/${jobId}/upload-url`, body);
}

export async function completeJobDocumentUpload(
  jobId: number,
  body: JobDocumentCompleteBody,
): Promise<JobDocument> {
  return apiClient.post<JobDocument>(`/jobs/documents/${jobId}/complete`, body);
}

export async function getJobDocumentDownloadUrl(
  jobId: number,
  documentId: number,
  disposition: 'inline' | 'attachment',
): Promise<JobDocumentDownloadUrlResponse> {
  return apiClient.get<JobDocumentDownloadUrlResponse>(
    `/jobs/documents/${jobId}/${documentId}/download-url?disposition=${disposition}`,
  );
}

/**
 * Resolve a presigned download URL for an attachment by its S3 key rather than a
 * document id. Used by the Activity Log / Messages attachment links, which only
 * carry the S3 key from the event payload. The backend looks up (or self-heals)
 * the job_file row and presigns it, so this works even when the document was
 * never mirrored into the documents list.
 */
export async function resolveJobDocumentDownloadUrl(
  jobId: number,
  s3Key: string,
  disposition: 'inline' | 'attachment',
): Promise<JobDocumentDownloadUrlResponse> {
  const params = new URLSearchParams({ s3Key, disposition });
  return apiClient.get<JobDocumentDownloadUrlResponse>(
    `/jobs/documents/${jobId}/resolve-download-url?${params.toString()}`,
  );
}
